import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Sparkles } from 'lucide-react';
import { fetchBySlug } from '../../lib/cms';
import SEO from '../../components/SEO';

export default function NyhedDetaljeEn() {
  const { slug } = useParams();
  const [nyhed, setNyhed] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      const data = await fetchBySlug(slug, 'en');
      setNyhed(data);
      setLoading(false);
    }
    loadData();
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-900">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#0E67A2] border-t-transparent" />
      </div>
    );
  }

  if (!nyhed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 text-slate-900 px-4 text-center">
        <SEO title="Article Not Found" description="The requested article does not exist." path={`/en/news/${slug}`} lang="en" />
        <h1 className="text-3xl font-bold">Article Not Found</h1>
        <p className="mt-2 text-slate-500">The news item you are looking for does not exist or has been moved.</p>
        <Link to="/en/news" className="mt-6 rounded-xl bg-[#0E67A2] px-6 py-3 font-bold text-white transition hover:bg-[#0E67A2]/90">
          Back to news overview
        </Link>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: nyhed.title,
    datePublished: nyhed.date,
    author: {
      '@type': 'Organization',
      name: 'Danshaker',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Danshaker',
      logo: {
        '@type': 'ImageObject',
        url: 'https://assets.auxodev.dk/assets/e0cd175c-a113-44c2-9ab9-d86f3c298af2/14d0f8cb-56b7-4a51-8379-6cfdf86af873.png',
      },
    },
    description: nyhed.content ? nyhed.content.replace(/<[^>]*>?/gm, '').slice(0, 150) : '',
  };

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900 antialiased">
      <SEO
        title={nyhed.title}
        description={nyhed.content ? nyhed.content.replace(/<[^>]*>?/gm, '').slice(0, 160) : ''}
        path={`/en/news/${slug}`}
        lang="en"
        type="article"
        schema={articleSchema}
      />

      {/* Background gradients */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(14,103,162,0.05),transparent_60%)]" />

      <article className="mx-auto max-w-4xl px-4 pt-16 pb-24 md:px-8 md:pt-24">
        {/* Back Link */}
        <div className="mb-8">
          <Link
            to="/en/news"
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 transition hover:text-[#0E67A2]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to news overview
          </Link>
        </div>

        {/* Article Header */}
        <header className="space-y-4 border-b border-slate-200 pb-8">
          <div className="flex items-center gap-2 text-sm font-bold text-[#0E67A2]">
            <Calendar className="h-4 w-4" />
            <span>{nyhed.date}</span>
          </div>

          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl md:text-5xl leading-tight">
            {nyhed.title}
          </h1>
        </header>

        {/* Article Body */}
        <div className="prose prose-slate max-w-none pt-8 space-y-4 text-base md:text-lg leading-relaxed text-slate-700">
          <div dangerouslySetInnerHTML={{ __html: nyhed.content }} />
        </div>

        {/* Footer / CTA in Article */}
        <div className="mt-12 rounded-3xl border border-slate-200 bg-white p-8 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-bold text-slate-900 text-lg">Want to experience Danshaker live?</h3>
            <p className="text-slate-600 text-sm font-medium">Book a free and non-binding trial in your own stable facility.</p>
          </div>
          <Link
            to="/en/free-demo"
            className="rounded-xl bg-[#0E67A2] px-6 py-3 font-bold text-white shadow-md transition hover:bg-[#0E67A2]/90 shrink-0"
          >
            Book free demo
          </Link>
        </div>
      </article>
    </div>
  );
}
